import { Injectable } from '@angular/core';
import { Campaign } from 'app/models/campaign2.model';
import { Observable } from 'rxjs/Observable';
import { publishReplay, refCount } from 'rxjs/operators';
import { ListMetadata } from './../../models/list-metadata.model';
import { HttpClientService } from './../httpClient/httpclient.service';

@Injectable()
export class Campaign2Service {
  baseUrl = 'koncentro-promo/campaign';
  campaignUrl = 'koncentro-promo';
  loyaltyUrl = 'koncentro-loyalty';
  paramKeyCampaignType = 'DEFAULT.CAMPAIGN_TYPE';
  paramKeyBarcodeType = 'COUPON_DEFAULT_BARCODE_TYPE';
  cachedCampaignFields: Observable<Campaign>;
  generateCouponUrl = 'koncentro-promo/campaign/message-instance/generateCoupon';
  priorityOnline: any[] = [];
  couponId: string;
  customerId: string;
  clientCustomerId: string;


  constructor(
    private http: HttpClientService
  ) { }

  /**
   * Restituisce la lista delle Campagne
   */
  list(
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<any>, metadata: ListMetadata }> {
    const params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);

    return this.http.getPaged(this.baseUrl + '?' + params.toString());
  }

  /**
   * Restituisce la Campagna con id = campaignId
   * @param campaignId
   */
  getCampaign(campaignId: number) {
    return this.http.get(this.baseUrl + `?id=${campaignId}`);
  }

  /**
   * Restituisce le liste campagne
   */
  getCampaignList() {
    return this.http.get(`${this.baseUrl}`);
  }

  /**
   * Restituisce le azioni di una campagna, tramite il suo id
   * @param campaignId id della campagna
   */
  getCampaignAction(campaignId: number) {
    return this.http.get(this.baseUrl + '/campaign_action' + '/' + campaignId);
  }

  /**
   * Restituisce la lista delle azione di una campagne, tramite nome delle azioni
   */
  filterCampaignAction(
    campaignId,
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<any>, metadata: ListMetadata }> {
    const params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);
    return this.http.getPaged(this.baseUrl + '/campaign_action' + '/' + campaignId + '?' + params.toString());
  }

  /**
 * Restituisce un azione di una campagna, tramite il suo id
 * @param campaignId id della campagna
 * @param campaignActionId id dell'azione
 */
  getCampaignActionById(campaignId: number, campaignActionId: number) {
    return this.http.get(this.baseUrl + `/campaign_action/${campaignId}?id=${campaignActionId}`);
  }

  /**
    * Tramite metodo GET carico i campi del dto di Campaign2 in un Observable
    * @returns il dto campaign2
    */
  getCampaignFields() {
    if (!this.cachedCampaignFields) {
      this.cachedCampaignFields = this.http.get(`${this.campaignUrl}/schema/CampaignDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedCampaignFields;
  }

  /**
   * Restituisce la lista dei tipi di budget
   * @returns lista dei tipi di budget
   */
  getBudgetType() {
    return this.http.get(this.baseUrl + '/budget-type');
  }


  /**
   * Crea una Nuova Campagna
   * @param campaign l'oggetto di tipo Campagna da creare
   * @returns  ritorna l'oggetto di tipo CDampagna creato
   */
  createCampaign(campaign: Campaign) {
    return this.http.post(this.baseUrl, campaign);
  }

  /**
   * Aggiorna una campagna già esistente
   * @param campaign l'oggetto di tipo Campagna modificato
   * @param id l'id della Campagna da modificare
   * @returns ritorna la Campagna modificata
   */
  updateCampaign(campaign: Campaign, id: number) {
    return this.http.put(this.baseUrl + `/${id}`, campaign);
  }

  /**
   * Ritorna l'id del tipo campagna dalla param
   */
  getParamCampaignTypeId() {
    return this.http.get(this.loyaltyUrl + '/params/getParam/' + this.paramKeyCampaignType);
  }

  /**
   * Ritorna il barcode type pid dalla param
   */
  getParamBarcodeTypePid() {
    return this.http.get(this.loyaltyUrl + '/params/getParam/' + this.paramKeyBarcodeType);
  }

  /**
   * Restituisce i tipi di coupon selezionabili
   */
  getCampaignMessage(message: string) {
    return this.http.get(`${this.baseUrl}/message?assignement_entity=${message}`);
  }

  /**
   * Restituisce la lista degli action type
   * @returns ritorna la lista di action type
   */
  getActionTypeList() {
    return this.http.get(this.baseUrl + '/action_type');
  }

  /**
   * Ritorna il template di una determinata action
   * @param actionId l'id dell'action di cui si vuole il template
   * @returns ritorna un template
   */
  getActionTemplate(actionId: number) {
    return this.http.get(this.baseUrl + `/action_type/template/${actionId}`);
  }

  /**
   * Restituisce i dati per il form di emissione del coupon
   */
  getCouponEmissionData(campaignId: string, actionId: string) {
    return this.http.get(`${this.baseUrl}/campaign_action/${campaignId}?id=${actionId}`);
  }

  /**
   * Restituisce la lista dei touchpoints selezionabili
   * per la distribuzione del coupon
   */
  getTouchPoints(couponScope: string, couponActionType: string) {
    return this.http.get(`koncentro-promo/touch-point?flg_coupon_scope=${couponScope}&flg_coupon_action=${couponActionType}`);
  }

  /**
   * Restituisce i touchpoint e le relative priorità
   */
  getChannelPriorities(actionId: string, type: string) {
    return this.http.get(`${this.baseUrl}/campaign_action/ochannel?campaign_action_id=${actionId}&type=${type}`);
  }

  getChannelByCampaignAction(actionId: string) {
    return this.http.get(`${this.baseUrl}/campaign_action/ochannel?campaign_action_id=${actionId}`);
  }

  getMessageInstance(messageInstanceId: number) {
    return this.http.get(`${this.baseUrl}/message-instance?id=${messageInstanceId}`);
  }

  /**
   * Restituisce la lista dei filtri Area Geografica per i Negozi
   */
  getAreaGeo() {
    return this.http.get('/koncentro-common/store/getAreaGeo');
  }

  /**
   * Restituisce la lista dei filtri Insegne per i Negozi
   */
  getInsegna() {
    return this.http.get('/koncentro-common/store/getInsegna');
  }

  /**
   * Restituisce la lista dei filtri Gruppi per i Negozi
   */
  listGroups() {
    return this.http.get('/koncentro-common/store-group');
  }

  /**
   * Restituisce la lista dei negozi associabili alla Campagna, filtrandoli a seconda dei parametri inviati.
   * Se nel parametro "codici" di "filters" sono presenti store_code, restituisce i negozi già associati alla Campagna
   * @param filters
   */
  getStoresFromFilters(filters: object) {
    return this.http.post('/koncentro-common/store/stores_from_campaigns?limit=999999', filters);
  }

  /**
   * Crea un nuovo Gruppo di Negozi
   * @param groupData
   */
  createGroup(groupData) {
    return this.http.post('/koncentro-common/store-group', groupData);
  }

  /**
   * Aggiunge gli stores selezionati al gruppo con groupid
   * @param groupId
   * @param stores
   */
  setStoresInGroup(groupId, stores) {
    return this.http.post(`/koncentro-common/store-group/${groupId}/store`, stores);
  }

  /**
   * Restituisce la lista dei negozi di un Gruppo
   * @param groupdId
  */
  listStoresInGroup(groupdId) {
    return this.http.get(`/koncentro-common/store-group/${groupdId}/store`);
  }

  /**
   * Restituisce la lista dei Gruppi di Negozi
   */
  listGroupsWithStores() {
    return this.http.get('/koncentro-common/store-group/stores');
  }

  /**
   * Restituisce le liste clienti
   */
  getCustomerLists() {
    return this.http.get(`${this.baseUrl}/distribution_list/list`);
  }

  saveEmissionDates(data: any) {
    return this.http.post(`${this.baseUrl}/campaign_action`, data);
  }

  updateEmissionDates(data: any) {
    return this.http.put(`${this.baseUrl}/campaign_action`, data);
  }

  saveCouponEmissionData(data: any) {
    return this.http.post(`${this.baseUrl}/message_action_complete`, data);
  }

  updateCouponEmissionData(messageInstanceId: number, data: any) {
    return this.http.put(`${this.baseUrl}/message_action_complete/${messageInstanceId}`, data);
  }

  /**
   * Restituisce i negozi associati alla action
   */
  getTouchpointStores(messageInstanceId: number, touchpointId: string, scope: string) {
    if (scope === 'fruition') {
      return this.http.get(`${this.baseUrl}/store-message-ochannel?message_instance_id=${messageInstanceId}&ochannel_id=${touchpointId}&store_type=F`);
    } else if (scope === 'emission') {
      return this.http.get(`${this.baseUrl}/store-message-ochannel?message_instance_id=${messageInstanceId}&ochannel_id=${touchpointId}&store_type=E`);
    }
    return this.http.get(`${this.baseUrl}/store-message-ochannel?message_instance_id=${messageInstanceId}&ochannel_id=${touchpointId}`);

  }

  getMessageInstanceData(messageInstanceId: string) {
    return this.http.get(`${this.baseUrl}/message-instance?id=${messageInstanceId}`);
  }

  getCouponParametersDefaultData(messageId: number) {
    return this.http.get(`${this.baseUrl}/message/${messageId}`);
  }

  getDtoSchemas(schema: string) {
    return this.http.get('koncentro-promo/schema/' + schema + 'Dto');
  }

  /**
   * Restituisce i campi del form di configurazione dei parametri del coupon
   */
  getCouponParamSetupFields(messageId: number, actionTypeCode: string) {
    return this.http.get(`${this.baseUrl}/template_assignement/list?assigned_to_id=${messageId}&assignement_entity=${actionTypeCode}`);
  }

  getPromoTypes(messageTypeId: number) {
    return this.http.get(`${this.baseUrl}/promo-type?message_type_id=${messageTypeId}`);
  }

  getConstraints(type: string) {
    return this.http.get(`${this.baseUrl}/campaign-constraint/list?constraint_type=${type}`);
  }

  getConstraintsData(messageInstanceId: string) {
    return this.http.get(`${this.baseUrl}/message-instance/constraint/${messageInstanceId}/list`);
  }

  getBasketDescriptionByMessageId(messageId: number) {
    return this.http.get(`${this.baseUrl}/message/${messageId}/basket`);
  }

  getBasketDescriptionByBasketId(basketId: number) {
    return this.http.get(`${this.baseUrl}/message-instance/basket/${basketId}`);
  }

  /**
   * Restituisce la lista dei Panieri, ed è possibile filtrarli per "description"
   * @param basketDesc
   */
  getBasketList(basketDesc?: string) {
    return this.http.get(`${this.baseUrl}/message-instance/basket?notdefault=true&description=${basketDesc}`);
  }

  saveCouponParametersData(data: any) {
    return this.http.post(`${this.baseUrl}/message-instance/layout`, data);
  }

  updateCouponParametersData(data: any, messageInstanceId: number, actionTypeCode: any) {
    return this.http.put(`${this.baseUrl}/message-instance/layout/${messageInstanceId}?actionTypeCode=${actionTypeCode}`, data);
  }

  saveNewBasket(messageInstanceId: string, data: any) {
    return this.http.post(`${this.baseUrl}/message-instance/${messageInstanceId}/basket_complete`, data);
  }

  saveMessageComplete(messageInstanceId: string, data: any) {
    return this.http.put(`${this.baseUrl}/message-complete/${messageInstanceId}`, data);
  }

  generateCoupon(data: any) {
    return this.http.post(this.generateCouponUrl, data);
  }

  distributionListCount(data: any) {
    return this.http.post(`${this.baseUrl}/campaign_distribution_list/count`, data);
  }

  publish(data: any) {
    return this.http.post(`${this.baseUrl}/message-instance/publishMessageInstance`, data);
  }

  deleteAction(actionId: number) {
    return this.http.delete(`${this.baseUrl}/campaign_action/${actionId}`);
  }

  /**
   * Restituisce i touchpoint di emissione di una action, con i relativi form schema di layout
   */
  getEmissionTouchpointsAndLayouts(actionId: number) {
    return this.http.get(`${this.baseUrl}/layout_config/${actionId}`);
  }
  getEmissionTouchpointAndLayoutsDefault(messageInstanceId: number) {
    return this.http.get(`${this.baseUrl}/layout_config/default/${messageInstanceId}`);
  }

  saveTouchpointLayout(actionId: number, layoutId: number, channelId: number, data: any) {
    return this.http.put(`${this.baseUrl}/campaign_action/saveJsonLayout/${actionId}/${layoutId}/${channelId}`, data);
  }

  getWorkflowList() {
    return this.http.get('koncentro-loyalty/params?param_group=ExternalWorkflow');
  }

  saveExternalWorkflowLayout(messageInstanceId: number, externalWorkflow: any) {
    return this.http.put(`${this.baseUrl}/message-instance/saveExternalWorkflowLayout/${messageInstanceId}`, { externalWorkflow });
  }

  saveTouchpointDefaultLayout(messageIstanceId: number, layoutId: number, data: any) {
    return this.http.put(`${this.baseUrl}/message-instance/saveJsonLayout/${messageIstanceId}/${layoutId}`, data);
  }

  preview(messageIstanceId: number, campaignId: number) {
    return this.http.post(`${this.baseUrl}/message-instance/previewMessageInstance`, {
      message_instance_id: messageIstanceId,
      campaign_id: campaignId
    });
  }

  duplicateCampaignAction(data?: any) {
    return this.http.post(`${this.baseUrl}/campaign_action/duplicateAction`, data);
  }

  viewCampaignAction(campaignId: number, messageInstanceId: number, campaignActionId: number) {
    return this.http.get(`${this.baseUrl}/campaign_action/viewCampaignAction/${campaignId}/${messageInstanceId}/${campaignActionId}`)
  }

  getCouponByCustomerId(customerId: number): Observable<{ data: Array<any>, metadata: ListMetadata }> {
    return this.http.getPaged(`${this.baseUrl}/message-instance/coupon?limit=99999&customer_id=${customerId}`)
  }

  deployAction(message_instance_id: number) {
    return this.http.put(`${this.baseUrl}/deployAction/${message_instance_id}`, {})
  }

  getProductItemList() {
    return this.http.get(`/koncentro-common/product-item`);
  }

  getProductItemByHostCode(productItem?: string) {
    return this.http.get(`/koncentro-common/product-item?product_item=${productItem}`);
  }

  getProductItemBarcodeById(productItemId?: string) {
    return this.http.get(`/koncentro-common/product-item/barcode?product_item_id=${productItemId}`);
  }

  checkBarcodeAvailability(campaignId: any, barcode?: string, messageInstanceId?: any, data?) {
    if (messageInstanceId === undefined || messageInstanceId === null)
      return this.http.post(`${this.baseUrl}/campaign_action/barcodeAvailability/${campaignId}?barcode=${barcode}`, data);
    else
      return this.http.post(`${this.baseUrl}/campaign_action/barcodeAvailability/${campaignId}?barcode=${barcode}&message_instance_id=${messageInstanceId}`, data);
  }

  getProductDepartmentList() {
    return this.http.get(`/koncentro-common/product-department`);
  }

  getProductDepartmentByBasket(basketId?: any) {
    return this.http.get(`/koncentro-common/product-department?basket_id=${basketId}`);
  }

  getBasketDetailByBasketId(basketId: any) {
    return this.http.get(`${this.baseUrl}/message-instance/basket-detail?basket_id=${basketId}`);
  }

  createBasketDetail(data) {
    return this.http.post(`${this.baseUrl}/message-instance/basket-detail`, data);
  }

  updateBasketDetail(basketId: any, data?) {
    return this.http.put(`${this.baseUrl}/message-instance/basket-detail/${basketId}`, data);
  }

  removeBasketDetail(basketDetailId: any) {
    return this.http.delete(`${this.baseUrl}/message-instance/basket-detail/${basketDetailId}`);
  }

  createBasket(messageInstanceId: string, data) {
    return this.http.post(`${this.baseUrl}/message-instance/${messageInstanceId}/basket`, data);
  }

  getTableauData() {
    return this.http.get('koncentro-api/tableau/token');
  }

  getCampaignAcmList(messageInstanceId: string) {
    return this.http.get(`${this.baseUrl}/message-channel-acm?message_instance_id=${messageInstanceId}`);
  }

  createAcm(acmData: any) {
    return this.http.post(`${this.baseUrl}/message-channel-acm`, acmData);
  }

  updateAcm(acmData: any, id: number) {
    return this.http.put(`${this.baseUrl}/message-channel-acm/${id}`, acmData);
  }

  deleteAcm(id: number) {
    return this.http.delete(`${this.baseUrl}/message-channel-acm/${id}`);
  }

  getValidityCoupon(paramCode: string) {
    return this.http.get(`koncentro-loyalty/params?param_group=validity_coupon&param_code=${paramCode}`);
  }

  getMessageInstanceRule(messageInstanceId) {
    return this.http.get(`${this.baseUrl}/message-instance/message-instance-rule?message_instance_id=${messageInstanceId}`);
  }

  createMessageInstanceRule(data) {
    return this.http.post(`${this.baseUrl}/message-instance/message-instance-rule`, data);
  }

  updateMessageInstanceRule(id, data) {
    return this.http.put(`${this.baseUrl}/message-instance/message-instance-rule/${id}`, data);
  }

  deleteMessageInstanceRule(id) {
    return this.http.delete(`${this.baseUrl}/message-instance/message-instance-rule/${id}`);
  }

  deleteMessageInstanceRuleAndDetail(id) {
    return this.http.delete(`${this.baseUrl}/message-instance/message-instance-rule/detail/${id}`);
  }

  getMultipleCouponRuleType() {
    return this.http.get(`${this.baseUrl}/message-instance/message-instance-rule-type?code=CV`)
  }

  anteprimaCoupon(data) {
    return this.http.post(`koncentro-proxy/proxy/ibc/anteprimaCoupon/v1`, data);
  }


  /**
 * Restituisce la lista delle classificazioni dei negozi
 */
  getClassification() {
    return this.http.get('/koncentro-common/classification?classification_type_name=store');
  }

  /**
* Restituisce la lista dei negozi associabili alla Campagna, filtrandoli a seconda dei parametri inviati.
* Se nel parametro "codici" di "filters" sono presenti store_code, restituisce i negozi già associati alla Campagna
* Aggiunto come parametro di ricerca le classification
*  @param filters
*/
  getStoresFromFiltersByClassification(filters: object) {
    return this.http.post('/koncentro-common/store/v2/stores_from_campaigns?limit=999999', filters);
  }

  getTouchpointLayoutById(touchpointId: number) {
    return this.http.get(`${this.baseUrl}/layout_config?idOchannel=${touchpointId}`);
  }

  getTouchpointLayoutDefault() {
    return this.http.get(`${this.baseUrl}/layout_config?channel_default=true`);
  }

  createTouchpointLayoutConfig(layoutConfig: any) {
    return this.http.post(`${this.baseUrl}/layout_config/`, layoutConfig);
  }

  updateTouchpointLayoutConfig(layoutConfig: any) {
    return this.http.put(`${this.baseUrl}/layout_config/` + layoutConfig.id, layoutConfig);
  }
}


// WEBPACK FOOTER //
// ./src/app/services/campaign-2.0/campaign2.service.ts